import { isSameDay, startOfMonth } from '@myre/common-dates';
import { LangLocale, languages } from '@myre/common-i18n';
import { DateFnsAdapter, LANGLOCALE } from '@myre/ui-primitive-core';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  type OnInit,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { type MatCalendarCellCssClasses } from '@angular/material/datepicker';
import {
  addMonths,
  endOfMonth,
  getISOWeeksInYear,
  getMonth,
  getWeek,
  subMonths,
} from 'date-fns';

import { MyreMonthViewComponent } from '../myre-month-view/myre-month-view.component';

import { type CalendarEvent } from './myre-calendar.interface';

interface IMonth {
  startDate: Date;
  numberOfWeeksToAdd: number;
}

@Component({
  selector: 'myre-calendar',
  templateUrl: './myre-calendar.component.html',
  styleUrls: ['./myre-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
  ],
  imports: [MyreMonthViewComponent],
})
export class MyreCalendarComponent implements OnInit {
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);
  private _dateAdapter = inject<DateAdapter<Date>>(DateAdapter, {
    optional: true,
  });

  @Input()
  public set numberOfMonthsDisplayed(numberOfMonthsDisplayed: number) {
    this._numberOfMonthsDisplayed = numberOfMonthsDisplayed || 1;
  }

  @Input()
  public set startDate(startDate: Date) {
    this._startDate = startDate || new Date();
  }

  @Input()
  public selectedDates: Date[] = [];

  @Input()
  public minDate: Date;

  @Input()
  public maxDate: Date;

  @Input()
  public dateFilter: (date: Date) => boolean;

  @Input()
  public multiSelect: boolean = false;

  @Input()
  public set highlightedDates(highlightedDates: Date[]) {
    this._highlightedDates = highlightedDates;
  }

  @Input()
  public eventCalendar: boolean = false;

  @Input()
  public events = new Map<string, CalendarEvent[]>();

  public months: IMonth[] = [];

  @Input()
  public canEdit: boolean = false;

  @Output()
  public selectDates = new EventEmitter<Date[]>();

  @Output()
  public onEventCreated = new EventEmitter<Date>();

  @Output()
  public onEventUpdated = new EventEmitter<CalendarEvent>();

  private _numberOfMonthsDisplayed: number;
  private _highlightedDates: Date[] = [];
  private _startDate: Date;

  public ngOnInit(): void {
    this._dateAdapter.setLocale(this._langLocale);
  }

  public ngOnChanges(): void {
    this._generateMonths();
  }

  public onSelectDate(date: Date): void {
    if (this.selectedDates.some(d => isSameDay(date, d))) {
      this.selectDates.emit(
        this.selectedDates.filter(d => !isSameDay(date, d)),
      );
    } else {
      if (this.multiSelect === true) {
        this.selectDates.emit([...this.selectedDates, date]);
      } else {
        this.selectDates.emit([date]);
      }
    }
  }

  public dateClass(date: Date): MatCalendarCellCssClasses {
    if (date && this._highlightedDates.some(d => isSameDay(d, date))) {
      return 'highlighted-date';
    }
  }

  public trackByMonth(_: number, month: IMonth): number {
    return startOfMonth(month.startDate).valueOf();
  }

  public onEventCreatedEmit(date: Date): void {
    this.onEventCreated.emit(date);
  }

  public onEventUpdatedEmit(event: CalendarEvent): void {
    this.onEventUpdated.emit(event);
  }

  private _generateMonths(): void {
    let date = this._startDate;
    this.months = [];

    for (let i = 0; i < this._numberOfMonthsDisplayed; i++) {
      const currentMonth: IMonth = {
        startDate: date,
        numberOfWeeksToAdd: 0,
      };

      this.months.push(currentMonth);

      date = addMonths(date, 1);
    }

    this._equalizeNbOfWeeksPerMonth();
  }

  private _equalizeNbOfWeeksPerMonth(): void {
    let maxNumberOfWeeks = 0;

    for (const month of this.months) {
      const numberOfWeeks = this._numberOfWeeksInMonth(month);

      maxNumberOfWeeks =
        maxNumberOfWeeks < numberOfWeeks ? numberOfWeeks : maxNumberOfWeeks;

      // We temporally keep the number of weeks of the month in its numberOfWeeksToAdd attribute
      month.numberOfWeeksToAdd = numberOfWeeks;
    }

    this.months = this.months.map(m => ({
      ...m,
      // And then subtract to obtain the right value
      numberOfWeeksToAdd: maxNumberOfWeeks - m.numberOfWeeksToAdd,
    }));
  }

  private _numberOfWeeksInMonth(month: IMonth): number {
    const dateFirst = startOfMonth(month.startDate);
    const dateLast = endOfMonth(month.startDate);

    const startWeek = getWeek(dateFirst, {
      locale: languages[this._langLocale].dateFnsLocale,
    });
    const endWeek = getWeek(dateLast, {
      locale: languages[this._langLocale].dateFnsLocale,
    });

    if (endWeek < startWeek) {
      // In case last week of december or first week of january is counted on next/previous year weak account
      const lastYearNbWeeks =
        getMonth(dateFirst) === 0
          ? getISOWeeksInYear(subMonths(dateFirst, 1))
          : getISOWeeksInYear(dateFirst);

      return lastYearNbWeeks - startWeek + 1 + endWeek;
    }

    return endWeek - startWeek + 1;
  }
}
